<template>
  <app-page-container :custom="1600" :min-height="0">
    <app-page-header design="new" title="Crew imports" compact />
    <section class="ui-page">
      <app-filter-toolbar>
        <template #create><app-button design="new" :disabled="Boolean(activeImport)" @click.native="$modal.show('crew-import')">Import crew</app-button></template>
        <app-select v-model="status" design="new" aria-label="Status" :options="statuses" label="label" placeholder="Status" />
        <app-select v-model="mode" design="new" aria-label="Mode" :options="modes" label="label" placeholder="Mode" />
        <template v-if="status || mode" #clear><app-clear-filters-button design="new" icon-only @clear="clearFilters" /></template>
      </app-filter-toolbar>
      <active-import-notice @change="activeImport = $event" />
      <app-content-skeleton v-if="loading" label="Loading imports" :columns="columns" min-width="1200px" />
      <app-error-state v-else-if="error" :message="error" @retry="load" />
      <app-card v-else-if="items.length" design="new" variant="table" header-density="compact">
        <app-data-table design="new" density="dense" :columns="columns" :rows="items" clickable min-width="1200px" @row-click="openImport($event.row)">
          <template #cell-created_at="{ row }"><app-table-text :text="formatDateTime(row.created_at)" /></template>
          <template #cell-created_by="{ row }"><app-table-user :name="row.created_by ? userName(row.created_by) : ''" :photo="row.created_by && row.created_by.crop && row.created_by.crop.crop_url || ''" /></template>
          <template #cell-file_name="{ row }"><app-table-text :text="row.file_name" /></template>
          <template #cell-mode="{ row }"><app-table-text :text="modeLabel(row.mode)" /></template>
          <template #cell-status="{ row }"><import-status-badge compact :status="row.status" /></template>
          <template #cell-rows="{ row }"><app-table-text :text="rowsSummary(row)" /></template>
        </app-data-table>
      </app-card>
      <app-no-results v-else design="new" title="No imports yet" text="" />
      <app-pagination v-if="!loading && !error && count" design="new" placement="embedded" :page="page" :pages="pages" :count="count" :visible-count="items.length" :page-size="pageSize" :loading="loading" @change="setPage" />
    </section>
    <crew-import-modal />
  </app-page-container>
</template>

<script>
import { getClimbingMountainCrewImports } from '@/http'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppTableText from '@/components/AppTableText.vue'
import AppTableUser from '@/components/AppTableUser.vue'
import ActiveImportNotice from './components/ActiveImportNotice'
import CrewImportModal from './components/CrewImportModal'
import ImportStatusBadge from './components/ImportStatusBadge'
import {
  formatDateTime,
  formatNumber,
  IMPORT_MODE,
  IMPORT_STATUS,
  userName
} from './importState'

const PAGE_SIZE = 20

export default {
  name: 'ClimbingMountainCrewImportList',
  components: { ActiveImportNotice, CrewImportModal, ImportStatusBadge, AppContentSkeleton, AppErrorState, AppTableText, AppTableUser },
  data() {
    return {
      activeImport: null,
      items: [],
      count: 0,
      pages: 1,
      page: 1,
      pageSize: PAGE_SIZE,
      loading: true,
      error: '',
      status: null,
      mode: null,
      requestId: 0,
      statuses: Object.keys(IMPORT_STATUS).map(value => ({ value, label: IMPORT_STATUS[value].label })),
      modes: Object.keys(IMPORT_MODE).map(value => ({ value, label: IMPORT_MODE[value] })),
      columns: [
        { key: 'created_at', label: 'Created', width: '170px' },
        { key: 'created_by', label: 'Created by', width: '180px' },
        { key: 'file_name', label: 'File', minWidth: '220px' },
        { key: 'mode', label: 'Mode', width: '140px' },
        { key: 'status', label: 'Status', width: '170px' },
        { key: 'rows', label: 'Rows', minWidth: '220px' }
      ]
    }
  },
  watch: {
    status() { this.onFilterChange() },
    mode() { this.onFilterChange() }
  },
  created() {
    this.load()
  },
  methods: {
    formatDateTime,
    userName,
    modeLabel(mode) { return IMPORT_MODE[mode] || mode },
    onFilterChange() {
      this.page = 1
      this.load()
    },
    setPage(page) {
      this.page = page
      this.load()
    },
    load() {
      const requestId = ++this.requestId
      this.loading = true
      this.error = ''
      getClimbingMountainCrewImports({
        page: this.page,
        page_size: this.pageSize,
        status: this.status ? this.status.value : undefined,
        mode: this.mode ? this.mode.value : undefined
      }).then(response => {
        if (requestId !== this.requestId) return
        this.items = response.data.results
        this.count = response.data.count
        this.pages = response.data.pages
      }).catch(() => {
        if (requestId === this.requestId) this.error = 'The imports could not be loaded.'
      }).finally(() => {
        if (requestId === this.requestId) this.loading = false
      })
    },
    clearFilters() {
      this.status = null
      this.mode = null
    },
    detailLink(row) {
      return { name: 'climbing-mountain-crew-import-detail', params: { importId: row.id } }
    },
    openImport(row) {
      this.$router.push(this.detailLink(row)).catch(() => {})
    },
    rowsSummary(row) {
      if (!row.total_rows) return '—'
      const parts = [
        `${formatNumber(row.total_rows)} rows`,
        `${formatNumber(row.created_count)} new`,
        `${formatNumber(row.updated_count)} updated`
      ]
      if (row.error_count) parts.push(`${formatNumber(row.error_count)} with errors`)
      return parts.join(' · ')
    }
  }
}
</script>
